/**
 * Shared booking calculation utilities.
 * Single source of truth for rental period and pricing logic.
 * Used by: BookingSidebar, BookingConfirmModal, BoatDetailView.
 */
import { applyDurationPricing, type DurationRule } from './duration-pricing';
import { seasonalForDate, seasonRowForDate, type SeasonalRow } from './seasonal-pricing';
import { findMultidayTier } from './multiday-pricing';

export interface RentalHoursSettings {
  opening: string;
  closing: string;
  halfDayCutoff: string;
}

export interface RentalPeriods {
  fullDays: number;
  halfDays: number;
  description: string;
}

export interface ExtraPricing {
  id: string;
  price: number;
  priceType: string;
}

export interface ExtrasBreakdown {
  extraId: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface SeasonalPriceEntry {
  start_date?: string | null;
  end_date?: string | null;
  // MM-GG ricorrente (fonte unica post-unificazione). Match via questi, non le date assolute.
  start_md?: string | null;
  end_md?: string | null;
  ranges?: unknown;
  price_per_day: number | string;
  price_half_day?: number | string | null;
  // Fasce prezzate (già markup-ate come le colonne). Sorgente unica in seasonalForDate.
  slots?: unknown;
  /** Prezzi noleggio multi-giorno di questa stagione (già marcati): [{min_days,max_days,price_total}]. */
  multiday_tiers?: unknown;
  name: string;
  priority?: number;
  is_active?: boolean;
}

export interface SeasonalDayBreakdown {
  date: string;
  price: number;
  seasonName: string | null;
  isHalfDay: boolean;
}

export interface PriceBreakdown {
  fullDays: number;
  halfDays: number;
  fullDayRate: number;
  halfDayRate: number;
  fullDayTotal: number;
  halfDayTotal: number;
  baseTotal: number;
  shortStaySurchargeAmount: number;
  shortStaySurchargePercent: number;
  extrasTotal: number;
  grandTotal: number;
  depositAmount: number;
  rentalDescription: string;
  extrasBreakdown: ExtrasBreakdown[];
  seasonalBreakdown?: SeasonalDayBreakdown[];
}

const DEFAULT_RENTAL_HOURS: RentalHoursSettings = {
  opening: '09:00',
  closing: '18:00',
  halfDayCutoff: '13:00',
};

/**
 * Calculate rental periods (full days and half days) based on dates and times.
 *
 * Rules:
 * - Same day: if spans both morning and afternoon = 1 full day, else 1 half day
 * - Multi-day:
 *   - First day: morning start = full day, afternoon start = half day
 *   - Middle days: always full days
 *   - Last day: afternoon end = full day, morning end = half day
 */
export function calculateRentalPeriods(
  startDate: Date | null,
  endDate: Date | null,
  startTime: string,
  endTime: string,
  rentalHours: RentalHoursSettings = DEFAULT_RENTAL_HOURS
): RentalPeriods {
  if (!startDate || !endDate) {
    return { fullDays: 0, halfDays: 0, description: '' };
  }

  const openingHour = parseInt(rentalHours.opening.split(':')[0]);
  const cutoffHour = parseInt(rentalHours.halfDayCutoff.split(':')[0]);
  const closingHour = parseInt(rentalHours.closing.split(':')[0]);
  const startHour = parseInt(startTime.split(':')[0]);
  const endHour = parseInt(endTime.split(':')[0]);

  const msPerDay = 1000 * 60 * 60 * 24;
  const diffDays = Math.floor((endDate.getTime() - startDate.getTime()) / msPerDay);

  let fullDays = 0;
  let halfDays = 0;

  if (diffDays === 0) {
    // Same day rental
    const startsInMorning = startHour < cutoffHour;
    const endsInAfternoon = endHour > cutoffHour;
    if (startsInMorning && endsInAfternoon) {
      fullDays = 1;
    } else {
      halfDays = 1;
    }
  } else {
    // Multi-day rental: first day
    const firstDayStartsInMorning = startHour <= openingHour || startHour < cutoffHour;
    if (firstDayStartsInMorning) {
      fullDays += 1;
    } else {
      halfDays += 1;
    }

    // Middle days (always full)
    if (diffDays > 1) {
      fullDays += diffDays - 1;
    }

    // Last day
    const lastDayEndsInAfternoon = endHour >= closingHour || endHour > cutoffHour;
    if (lastDayEndsInAfternoon) {
      fullDays += 1;
    } else {
      halfDays += 1;
    }
  }

  // Build description
  const parts: string[] = [];
  if (fullDays > 0) {
    parts.push(`${fullDays} ${fullDays === 1 ? 'giorno intero' : 'giorni interi'}`);
  }
  if (halfDays > 0) {
    parts.push(`${halfDays} ${halfDays === 1 ? 'mezza giornata' : 'mezze giornate'}`);
  }

  return { fullDays, halfDays, description: parts.join(' + ') };
}

/**
 * Calculate equivalent days for extras pricing.
 * Half days count as 0.5 equivalent days, rounded up for billing.
 */
export function getEquivalentDays(periods: RentalPeriods): number {
  return Math.ceil(periods.fullDays + periods.halfDays * 0.5);
}

/**
 * Calculate extras total cost based on rental periods and guests.
 */
export function calculateExtrasTotal(
  extras: ExtraPricing[],
  selectedExtraIds: string[],
  periods: RentalPeriods,
  guests: number
): { total: number; breakdown: ExtrasBreakdown[] } {
  const equivalentDays = getEquivalentDays(periods);
  const breakdown: ExtrasBreakdown[] = [];
  let total = 0;

  for (const extra of extras) {
    if (!selectedExtraIds.includes(extra.id)) continue;

    const price = Number(extra.price) || 0;
    let extraTotal = 0;

    switch (extra.priceType) {
      case 'per_day':
        extraTotal = price * equivalentDays;
        break;
      case 'per_person':
        extraTotal = price * guests;
        break;
      case 'per_person_per_day':
        extraTotal = price * guests * equivalentDays;
        break;
      default: // per_stay
        extraTotal = price;
        break;
    }

    total += extraTotal;
    breakdown.push({
      extraId: extra.id,
      quantity: 1,
      unitPrice: price,
      totalPrice: extraTotal,
    });
  }

  return { total, breakdown };
}

/**
 * Get the best matching seasonal price for a specific date.
 * Returns null if no season matches (use base price).
 */
function getSeasonForDate(
  dateStr: string,
  seasonalPrices: SeasonalPriceEntry[]
): {
  pricePerDay: number;
  priceHalfDay: number | null;
  priceHalfMorning: number | null;
  priceHalfAfternoon: number | null;
  name: string;
} | null {
  // Resolver MM-GG unico (gestisce ranges + wrap dic→gen). Vedi lib/seasonal-pricing.
  return seasonalForDate(dateStr, seasonalPrices as unknown as SeasonalRow[]);
}

/**
 * Prezzo di una singola data per parte di giornata (intera vs mezza), SEASONAL-AWARE.
 * Sorgente unica per: modali "Mezze giornate" (BookingSidebar / MobileBookingFlow),
 * customBreakdown per-day, e validazione server (POST /api/bookings, path dayParts).
 * Client e server chiamano lo STESSO helper → nessun drift → booking accettato.
 *
 * Precedenza:
 *  - GIORNATA INTERA: override per-data (boat_date_prices, già markupped — copre sia
 *    l'override manuale del vendor sia il seasonal "virtuale") › stagione › base barca.
 *  - MEZZA GIORNATA: stagione (`price_half_day`, fallback 60% del giorno) › half base
 *    barca. (`dailyOverrides` contiene solo il prezzo full, niente half → ignorato qui.)
 *
 * `seasonalRows` e i base price devono essere già markupped (catalogo `?with_markup=1`
 * lato client; `applyMarkup` lato server), così i due lati combaciano.
 */
/**
 * `part`:
 *  - `false` / `'full'`      → giornata intera
 *  - `true`                  → mezza GENERICA (floor `priceHalfDay`) — path display legacy
 *  - `'morning'`/`'afternoon'` → mezza del LATO (F2a): chi sceglie il pomeriggio paga il
 *    pomeriggio, non il minimo tra le due mezze. Fallback a `priceHalfDay` se il lato manca.
 */
export function dayPartPrice(
  dateStr: string,
  part: boolean | 'full' | 'morning' | 'afternoon',
  opts: {
    dailyOverrides?: Record<string, number>;
    /** Override manuale mezza giornata per data (boat_date_prices.rule_prices.half_day).
     *  Priorità massima sulla mezza, come dailyOverrides per il full. */
    halfOverrides?: Record<string, number>;
    seasonalRows?: SeasonalPriceEntry[] | null;
    fullBase: number;
    halfBase: number;
  },
): number {
  const isFull = part === false || part === 'full';
  const season = getSeasonForDate(dateStr, (opts.seasonalRows ?? []) as SeasonalPriceEntry[]);
  if (!isFull) {
    const halfOv = opts.halfOverrides?.[dateStr];
    if (halfOv != null && halfOv > 0) return halfOv; // override manuale half — priorità massima
    if (season) {
      const side = part === 'morning' ? season.priceHalfMorning
        : part === 'afternoon' ? season.priceHalfAfternoon
        : null; // true (mezza generica) → floor priceHalfDay
      return side ?? season.priceHalfDay ?? Math.round(season.pricePerDay * 0.6);
    }
    return opts.halfBase;
  }
  const ov = opts.dailyOverrides?.[dateStr];
  if (ov != null && ov > 0) return ov;
  if (season) return season.pricePerDay;
  return opts.fullBase;
}

/**
 * Calculate day-by-day seasonal pricing for a booking.
 */
function calculateSeasonalBaseTotal(
  startDate: Date,
  endDate: Date,
  startTime: string,
  endTime: string,
  basePricePerDay: number,
  baseHalfDay: number,
  seasonalPrices: SeasonalPriceEntry[],
  rentalHours: RentalHoursSettings = DEFAULT_RENTAL_HOURS,
  dailyOverrides: Record<string, number> = {},
  halfOverrides: Record<string, number> = {},
): { total: number; breakdown: SeasonalDayBreakdown[] } {
  const cutoffHour = parseInt(rentalHours.halfDayCutoff.split(':')[0]);
  const openingHour = parseInt(rentalHours.opening.split(':')[0]);
  const closingHour = parseInt(rentalHours.closing.split(':')[0]);
  const startHour = parseInt(startTime.split(':')[0]);
  const endHour = parseInt(endTime.split(':')[0]);

  const msPerDay = 1000 * 60 * 60 * 24;
  const diffDays = Math.floor((endDate.getTime() - startDate.getTime()) / msPerDay);

  // Build list of days col LATO (full/morning/afternoon) → prezzo mezza distinto per lato
  // (parità con priceForSpan/dayPartPrice, F2b). single-day: morning se finisce entro il
  // cutoff, altrimenti afternoon. multi-day: primo giorno parziale = afternoon (parte tardi),
  // ultimo = morning (finisce presto).
  const days: { date: string; part: 'full' | 'morning' | 'afternoon' }[] = [];
  const toStr = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  if (diffDays === 0) {
    const startsInMorning = startHour < cutoffHour;
    const endsInAfternoon = endHour > cutoffHour;
    const isFull = startsInMorning && endsInAfternoon;
    days.push({ date: toStr(startDate), part: isFull ? 'full' : (startHour < cutoffHour ? 'morning' : 'afternoon') });
  } else {
    const firstMorning = startHour <= openingHour || startHour < cutoffHour;
    days.push({ date: toStr(startDate), part: firstMorning ? 'full' : 'afternoon' });

    for (let i = 1; i < diffDays; i++) {
      const d = new Date(startDate.getTime() + i * msPerDay);
      days.push({ date: toStr(d), part: 'full' });
    }

    const lastAfternoon = endHour >= closingHour || endHour > cutoffHour;
    days.push({ date: toStr(endDate), part: lastAfternoon ? 'full' : 'morning' });
  }

  // Price each day
  let total = 0;
  const breakdown: SeasonalDayBreakdown[] = [];

  for (const { date, part } of days) {
    const isHalfDay = part !== 'full';
    const override = dailyOverrides[date];
    const season = getSeasonForDate(date, seasonalPrices);
    // Mezza del LATO reale (morning/afternoon) → parità con priceForSpan (F2b); fallback
    // al floor priceHalfDay, poi 60% del giorno.
    const sideHalf = season
      ? (part === 'morning' ? season.priceHalfMorning : part === 'afternoon' ? season.priceHalfAfternoon : null)
      : null;
    let price: number;
    let seasonName: string | null = null;
    // Precedenza per lato: override manuale › stagione › base.
    //  - full: dailyOverrides (boat_date_prices.price)
    //  - half: halfOverrides (boat_date_prices.rule_prices.half_day) — priorità massima
    if (isHalfDay) {
      const halfOv = halfOverrides[date];
      if (halfOv != null && halfOv > 0) {
        price = halfOv;
        seasonName = 'Tariffa mezza giornata';
      } else if (season) {
        price = sideHalf ?? season.priceHalfDay ?? Math.round(season.pricePerDay * 0.6);
        seasonName = season.name;
      } else {
        price = baseHalfDay;
      }
    } else {
      if (override != null && override > 0) {
        price = override;
        seasonName = 'Tariffa giornaliera';
      } else if (season) {
        price = season.pricePerDay;
        seasonName = season.name;
      } else {
        price = basePricePerDay;
      }
    }
    total += price;
    breakdown.push({ date, price, seasonName, isHalfDay });
  }

  return { total, breakdown };
}

/**
 * Calculate complete price breakdown for a booking.
 * This is the single source of truth for all pricing.
 * Supports optional seasonal pricing for day-by-day rate variation.
 */
export function calculatePriceBreakdown(
  startDate: Date | null,
  endDate: Date | null,
  startTime: string,
  endTime: string,
  pricePerDay: number,
  priceHalfDay: number | null | undefined,
  extras: ExtraPricing[],
  selectedExtraIds: string[],
  guests: number,
  rentalHours: RentalHoursSettings = DEFAULT_RENTAL_HOURS,
  depositPercent: number = 0.3,
  seasonalPrices: SeasonalPriceEntry[] = [],
  shortStaySurchargePercent: number = 0,
  rentalMode: string = 'daily',
  /**
   * Override prezzo per data specifica (boat_date_prices). Priorità: override
   * > seasonal > base. I valori sono già "client-facing" (markup applicato a
   * monte dal backend con ?with_markup=1).
   */
  dailyOverrides: Record<string, number> = {},
  /** Regole duration-based (sconto settimanale, ecc.) — dal boat API, override già markup-ato. */
  durationRules: DurationRule[] = [],
  /** Override manuale mezza giornata per data (boat_date_prices.rule_prices.half_day).
   *  Priorità massima sulla mezza (come dailyOverrides per il full). Già markup-ato. */
  halfOverrides: Record<string, number> = {},
): PriceBreakdown {
  const periods = calculateRentalPeriods(startDate, endDate, startTime, endTime, rentalHours);

  const halfDayRate = priceHalfDay || Math.round(pricePerDay * 0.6);

  let baseTotal: number;
  let seasonalBreakdown: SeasonalDayBreakdown[] | undefined;

  const hasOverrides = Object.keys(dailyOverrides).length > 0 || Object.keys(halfOverrides).length > 0;
  if (startDate && endDate && (seasonalPrices.length > 0 || hasOverrides)) {
    const seasonal = calculateSeasonalBaseTotal(
      startDate, endDate, startTime, endTime,
      pricePerDay, halfDayRate, seasonalPrices, rentalHours, dailyOverrides, halfOverrides
    );
    baseTotal = seasonal.total;
    seasonalBreakdown = seasonal.breakdown;
  } else {
    baseTotal = pricePerDay * periods.fullDays + halfDayRate * periods.halfDays;
  }

  // Se abbiamo seasonalBreakdown (override o seasonal applicati), ricomputiamo
  // i totali full/half-day dalla breakdown effettiva, altrimenti fallback flat.
  let fullDayTotal: number;
  let halfDayTotal: number;
  if (seasonalBreakdown && seasonalBreakdown.length > 0) {
    fullDayTotal = seasonalBreakdown.filter((d) => !d.isHalfDay).reduce((acc, d) => acc + d.price, 0);
    halfDayTotal = seasonalBreakdown.filter((d) => d.isHalfDay).reduce((acc, d) => acc + d.price, 0);
  } else {
    fullDayTotal = pricePerDay * periods.fullDays;
    halfDayTotal = halfDayRate * periods.halfDays;
  }

  const { total: extrasTotal, breakdown: extrasBreakdown } = calculateExtrasTotal(
    extras,
    selectedExtraIds,
    periods,
    guests
  );

  // Sconto duration-based (es. settimanale OREA): se una regola matcha durata+
  // data, ricalcola il base. Override già markup-ato (dal boat API) → coerente
  // col base markup-ato. Mutuamente esclusivo col short-stay surcharge sotto
  // (le durate lunghe non triggerano il surcharge).
  // Multi-giorno per-stagione (full-day rental): il tier è il TOTALE del noleggio
  // (price_total GIÀ marcato nei seasonalPrices dall'API detail) → scavalca il
  // per-day E le duration rules. IDENTICO al server (validateBookingPrices) →
  // niente drift. Solo full-day (halfDays==0).
  const mdCiYmd = startDate
    ? `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`
    : '';
  const mdRow = startDate && periods.halfDays === 0 && periods.fullDays >= 2
    ? seasonRowForDate(mdCiYmd, seasonalPrices as unknown as SeasonalRow[])
    : null;
  const mdTier = mdRow ? findMultidayTier((mdRow as { multiday_tiers?: unknown }).multiday_tiers, periods.fullDays) : null;
  if (mdTier) {
    baseTotal = mdTier.price_total;
  } else if (startDate && durationRules.length > 0) {
    const adj = applyDurationPricing({
      baseTotal, fullDays: periods.fullDays, halfDays: periods.halfDays,
      fullDayTotal, halfDayTotal, checkInYmd: mdCiYmd, rules: durationRules,
    });
    baseTotal = adj.adjustedBase;
  }

  // Short-stay surcharge: applies when booking is shorter than standard period
  const standardPeriod = rentalMode === 'weekly' ? 7 : rentalMode === 'monthly' ? 30 : 0;
  const totalDays = periods.fullDays + periods.halfDays;
  let shortStaySurchargeAmount = 0;
  if (shortStaySurchargePercent > 0 && standardPeriod > 0 && totalDays > 0 && totalDays < standardPeriod) {
    shortStaySurchargeAmount = Math.round(baseTotal * (shortStaySurchargePercent / 100));
  }

  const grandTotal = baseTotal + shortStaySurchargeAmount + extrasTotal;
  const depositAmount = Math.ceil(grandTotal * depositPercent);

  return {
    fullDays: periods.fullDays,
    halfDays: periods.halfDays,
    fullDayRate: pricePerDay,
    halfDayRate,
    fullDayTotal,
    halfDayTotal,
    baseTotal,
    shortStaySurchargeAmount,
    shortStaySurchargePercent: totalDays > 0 && totalDays < standardPeriod ? shortStaySurchargePercent : 0,
    extrasTotal,
    grandTotal,
    depositAmount,
    rentalDescription: periods.description,
    extrasBreakdown,
    seasonalBreakdown,
  };
}
